import { useQuery, type QueryClient } from '@tanstack/react-query';
import { AI_CREDIT_REFUSAL_HTTP_STATUS, type AiBilling } from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';

/** The wallet's cache key. It carries the project id, so a payment's per-project invalidation reaches it. */
export const creditsKey = (projectId: string) => ['ai-credits', projectId] as const;

/** The history's cache key (every page of it). */
export const creditTransactionsKey = (projectId: string) => ['ai-credit-transactions', projectId] as const;

/**
 * THE PRODUCTION'S WALLET, and with it where the platform stands on metered AI.
 *
 * `aiBilling` decides what every AI entry point does (design C.3, B.7):
 *   - LEGACY — exactly today's screens; no approval, no credits panel;
 *   - METERED — every paid AI operation is estimated and approved first;
 *   - SUSPENDED — no new AI starts ("AI analysis is paused").
 *
 * One query, one key, for every surface that asks — the Plan & billing panel,
 * the balance chip, the approval panel and the post-run summary all read the
 * same answer, so a top-up refreshes all of them at once.
 */
export function useProjectCredits(projectId: string | null) {
  return useQuery({
    queryKey: creditsKey(projectId ?? ''),
    queryFn: () => api.credits(projectId as string),
    enabled: projectId !== null && projectId !== '',
    // A balance somebody else spends changes under an open tab; short enough to
    // follow it, long enough that the chips on one screen share one request.
    staleTime: 15_000,
  });
}

/**
 * The mode a screen acts on. UNKNOWN READS AS LEGACY — the wallet still
 * loading, or an older API without the endpoint — because LEGACY is today's
 * behaviour and the SERVER is the gate: a metered production refuses the
 * legacy verb with `BILLING_APPROVAL_REQUIRED`, and every entry point turns
 * that refusal into the approval panel. Guessing METERED instead would hide a
 * working button behind an estimate the server cannot give.
 */
export function aiBillingMode(wallet: { aiBilling: AiBilling } | undefined): AiBilling {
  return wallet?.aiBilling ?? 'LEGACY';
}

/** Everything a credit movement makes stale: the wallet and every page of its history. */
export async function refreshCredits(queryClient: QueryClient, projectId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: creditsKey(projectId) }),
    queryClient.invalidateQueries({ queryKey: creditTransactionsKey(projectId) }),
  ]);
}

/**
 * The code an error carries — one of the contract's refusal codes
 * (`AiCreditRefusalCode`) or any other the API answers with — or null.
 */
export function refusalCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/**
 * The amounts a `CREDITS_INSUFFICIENT` refusal carries (S5): what is
 * available now and what this operation needs. Each is used only when the
 * body really carries an integer — a missing figure is left out, never drawn
 * as zero.
 */
export function insufficientAmounts(error: unknown): { availableCents: number | null; requiredCents: number | null } {
  if (!(error instanceof ApiError) || error.code !== 'CREDITS_INSUFFICIENT') {
    return { availableCents: null, requiredCents: null };
  }

  const body = (typeof error.detail === 'object' && error.detail !== null ? error.detail : {}) as Record<string, unknown>;
  const integer = (value: unknown): number | null => (Number.isInteger(value) ? (value as number) : null);

  return { availableCents: integer(body.availableCents), requiredCents: integer(body.requiredCents) };
}

/**
 * An answer that never arrived — the network, a 5xx, or one this page could not
 * read. Whether the server acted is unknown, so a retry repeats the SAME request
 * (same idempotency key) rather than making a new one.
 */
export function answerNeverArrived(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  // A coded refusal is an answer, whatever its status (PRICING_UNAVAILABLE is a 503).
  if (error.code !== null && Object.hasOwn(AI_CREDIT_REFUSAL_HTTP_STATUS, error.code)) return false;

  return error.status === 0 || error.status >= 500;
}
